import m from "mithril";
import { getUserData, getMithril, postMithril, showToast } from "../utils.js";
import { showConfirmation } from "../js/toaster.js";

var SavingsInterestPayment = {
    oninit: function (vnode) {
        vnode.state.products = [];
        vnode.state.loadingProducts = false;
        vnode.state.expandedProductId = null;
        
        vnode.state.accountsByProduct = {};
        vnode.state.loadingAccounts = {};
        vnode.state.expandedAccountId = null;
        
        vnode.state.isProcessing = false;
        
        this.fetchProducts(vnode);
    },

    fetchProducts: async function (vnode) {
        try {
            vnode.state.loadingProducts = true;
            m.redraw();
            
            const result = await getMithril('/api/savings-interest/products', { component: vnode.state });
            
            if (result.success) {
                vnode.state.products = result.data;
            } else {
                showToast(result.error || 'Gagal memuat produk simpanan', 'error');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showToast('Terjadi kesalahan saat memuat produk', 'error');
        } finally {
            vnode.state.loadingProducts = false;
            m.redraw();
        }
    },
    
    toggleProduct: async function (vnode, productId) {
        if (vnode.state.expandedProductId === productId) {
            vnode.state.expandedProductId = null;
            m.redraw();
            return;
        }
        
        vnode.state.expandedProductId = productId;
        vnode.state.expandedAccountId = null; // reset inner
        
        if (!vnode.state.accountsByProduct[productId]) {
            await this.fetchAccounts(vnode, productId);
        } else {
            m.redraw();
        }
    },
    
    fetchAccounts: async function(vnode, productId) {
        try {
            vnode.state.loadingAccounts[productId] = true;
            m.redraw();
            
            const result = await getMithril(`/api/savings-interest/accounts/${productId}`, { component: vnode.state });
            
            if (result.success) {
                vnode.state.accountsByProduct[productId] = result.data;
            } else {
                showToast(result.error || 'Gagal memuat detail rekening', 'error');
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            showToast('Terjadi kesalahan saat memuat detail rekening', 'error');
        } finally {
            vnode.state.loadingAccounts[productId] = false;
            m.redraw();
        }
    },
    
    toggleAccount: function(vnode, accountId) {
        if (vnode.state.expandedAccountId === accountId) {
            vnode.state.expandedAccountId = null;
        } else {
            vnode.state.expandedAccountId = accountId;
        }
        m.redraw();
    },

    payManual: async function (vnode, accountId, productId, targetDate, amount) {
        const formattedDate = this.formatDate(targetDate);
        showConfirmation(`Proses pembayaran jasa manual untuk bulan ${formattedDate}?`, async () => {
            try {
                vnode.state.isProcessing = true;
                m.redraw();
                
                const result = await postMithril('/api/savings-interest/pay-manual', {
                    accountId: accountId,
                    productId: productId,
                    targetDate: targetDate
                }, { component: vnode.state });
                
                if (result.success) {
                    showToast('Pembayaran jasa manual berhasil', 'success');
                    // Refresh the accounts list for this product to reflect the new state
                    await this.fetchAccounts(vnode, productId);
                } else {
                    showToast(result.error || 'Gagal memproses pembayaran', 'error');
                }
            } catch (error) {
                console.error('Error paying manually:', error);
                showToast('Terjadi kesalahan sistem saat pembayaran', 'error');
            } finally {
                vnode.state.isProcessing = false;
                m.redraw();
            }
        });
    },

    formatDate: function (dateString) {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '-';
            return date.toLocaleDateString('id-ID', { year: 'numeric', month: 'long' });
        } catch (error) {
            return '-';
        }
    },

    formatFullDate: function (dateString) {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '-';
            return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch (error) {
            return '-';
        }
    },

    formatCurrency: function (amount) {
        if (amount === null || amount === undefined || isNaN(amount)) return 'Rp 0';
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    },

    view: function (vnode) {
        return m("div", { class: "container mx-auto p-4" }, [
            m("div", { class: "flex justify-between items-center mb-6" }, [
                m("h1", { class: "text-2xl font-bold text-gray-800 dark:text-white" }, "Pembayaran Jasa (Manual)"),
                m("div", { class: "flex space-x-3" }, [
                    m("button", {
                        class: "bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors duration-200",
                        onclick: (e) => {
                            e.preventDefault();
                            this.fetchProducts(vnode);
                        }
                    }, [
                        m("i", { class: `ri-refresh-line mr-2 ${vnode.state.loadingProducts ? 'animate-spin' : ''}` }),
                        "Refresh"
                    ])
                ])
            ]),

            // Product Cards Overview
            vnode.state.loadingProducts && vnode.state.products.length === 0 ?
                m("div", { class: "flex justify-center my-12" }, [
                    m("i", { class: "ri-loader-4-line animate-spin text-4xl text-orange-600" })
                ]) :
            vnode.state.products.length === 0 ?
                m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg p-8 text-center" }, [
                    m("i", { class: "ri-hand-coin-line text-5xl text-gray-400 mb-4 inline-block" }),
                    m("h3", { class: "text-lg font-medium text-gray-900 dark:text-white mb-2" }, "Tidak Ada Produk Simpanan Aktif"),
                    m("p", { class: "text-gray-500 dark:text-gray-400" }, "Tidak dapat menemukan produk simpanan dengan suku bunga lebih dari 0%.")
                ]) :
                m("div", { class: "space-y-4" }, 
                    vnode.state.products.map(product => {
                        const isExpanded = vnode.state.expandedProductId === product.id;
                        const accounts = vnode.state.accountsByProduct[product.id] || [];
                        const accountsLoading = vnode.state.loadingAccounts[product.id];
                        
                        return m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden transition-all duration-200" }, [
                            // Product Header
                            m("div", { 
                                class: "px-6 py-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors",
                                onclick: () => this.toggleProduct(vnode, product.id)
                            }, [
                                m("div", { class: "flex items-center" }, [
                                    m("div", { class: "bg-orange-100 dark:bg-orange-900/50 p-3 rounded-full mr-4" }, [
                                        m("i", { class: "ri-bank-card-line text-xl text-orange-600 dark:text-orange-400" })
                                    ]),
                                    m("div", [
                                        m("h3", { class: "text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" }, [
                                            product.nama,
                                            // Badge for product type
                                            m("span", { class: "text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 font-medium" }, 
                                                product.is_berjangka ? "Berjangka" : "Biasa"
                                            )
                                        ]),
                                        m("p", { class: "text-sm text-gray-500 dark:text-gray-400 mt-1" }, `Suku Bunga: ${product.interest_rate}% per tahun`)
                                    ])
                                ]),
                                m("div", { class: "text-gray-400" }, [
                                    m("i", { class: `text-2xl transition-transform ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}` })
                                ])
                            ]),
                            
                            // Expanded Accounts Area
                            isExpanded ? m("div", { class: "border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 p-0" }, [
                                accountsLoading ? 
                                    m("div", { class: "flex justify-center py-8" }, [
                                        m("i", { class: "ri-loader-4-line animate-spin text-3xl text-orange-600" })
                                    ]) :
                                accounts.length === 0 ?
                                    m("div", { class: "py-6 text-center text-gray-500 dark:text-gray-400" }, "Tidak ada rekening anggota untuk produk ini.") :
                                    m("div", { class: "divide-y divide-gray-200 dark:divide-gray-750" }, 
                                        accounts.map((account, index) => {
                                            const accExpanded = vnode.state.expandedAccountId === account.id;
                                            
                                            // Calculate how many missing slots total to show badge
                                            const missingSlots = (account.interestHistory || []).filter(h => h.status === 'Missing').length;
                                            
                                            return m("div", { class: "bg-white dark:bg-gray-800" }, [
                                                // Account Row
                                                m("div", { 
                                                    class: "px-6 py-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750",
                                                    onclick: () => this.toggleAccount(vnode, account.id)
                                                }, [
                                                    m("div", { class: "flex-1" }, [
                                                        m("div", { class: "flex items-center gap-3 mb-1" }, [
                                                            m("span", { class: "font-semibold text-gray-800 dark:text-white" }, account.customer?.name || "Nasabah Tidak Diketahui"),
                                                            m("span", { class: "text-sm text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded" }, account.account_number),
                                                            
                                                            missingSlots > 0 ? 
                                                                m("span", { class: "text-xs font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded-full" }, `${missingSlots} Missed Jasa`) 
                                                                : m("span", { class: "text-xs font-bold px-2 py-0.5 bg-green-100 text-green-700 rounded-full" }, "Up to Date")
                                                        ]),
                                                        m("div", { class: "text-sm text-gray-600 dark:text-gray-400" }, [
                                                            "Saldo: ", m("span", { class: "font-medium" }, this.formatCurrency(account.balance))
                                                        ])
                                                    ]),
                                                    m("div", { class: "text-gray-400" }, [
                                                        m("i", { class: `text-xl transition-transform ${accExpanded ? 'ri-subtract-line' : 'ri-add-line'}` })
                                                    ])
                                                ]),
                                                
                                                // Expanded Interest History Sub-table
                                                accExpanded ? m("div", { class: "px-6 pb-4 pt-2 bg-gray-50 dark:bg-gray-800/50" }, [
                                                    m("div", { class: "text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" }, "Riwayat / Jadwal Pembayaran Jasa " + (product.is_berjangka ? "(Monthversary)" : "(Bulanan)")),
                                                    !account.interestHistory || account.interestHistory.length === 0 ?
                                                        m("p", { class: "text-sm text-gray-500 italic" }, "Belum ada riwayat perhitungan jasa yang tersedia.") :
                                                        m("div", { class: "overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700" }, [
                                                            m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm" }, [
                                                                m("thead", { class: "bg-gray-100 dark:bg-gray-700" }, [
                                                                    m("tr", [
                                                                        m("th", { class: "px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300" }, "Tanggal Tagihan"),
                                                                        m("th", { class: "px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300" }, "Status"),
                                                                        m("th", { class: "px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-300" }, "Estimasi/Tercatat"),
                                                                        m("th", { class: "px-4 py-2 text-center font-medium text-gray-600 dark:text-gray-300" }, "Aksi")
                                                                    ])
                                                                ]),
                                                                m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                                                                    account.interestHistory.map(historyItem => {
                                                                        let statusClass = "bg-gray-100 text-gray-800";
                                                                        let statusText = historyItem.status;
                                                                        
                                                                        if (historyItem.status === 'Paid') {
                                                                            statusClass = "bg-green-100 text-green-800";
                                                                            statusText = "Sudah Dibayar";
                                                                        } else if (historyItem.status === 'Missing') {
                                                                            statusClass = "bg-red-100 text-red-800";
                                                                            statusText = "Gagal / Terlewat";
                                                                        } else if (historyItem.status === 'Next Due') {
                                                                            statusClass = "bg-blue-100 text-blue-800";
                                                                            statusText = "Jadwal Mendatang";
                                                                        }
                                                                        
                                                                        return m("tr", { class: "hover:bg-gray-50 dark:hover:bg-gray-750" }, [
                                                                            m("td", { class: "px-4 py-2 text-gray-900 dark:text-gray-100" },
                                                                                m("div", { class: "flex flex-col gap-0.5" }, [
                                                                                    m("span", { class: "font-medium" }, this.formatDate(historyItem.targetDate)),
                                                                                    m("span", { class: "text-xs text-gray-500 dark:text-gray-400" }, this.formatFullDate(historyItem.targetDate)),
                                                                                    historyItem.isProrated ? m("span", { class: "text-[10px] bg-purple-100 text-purple-700 px-1 py-0.5 rounded w-fit" }, "Penyaluran Awal") : null
                                                                                ])
                                                                            ),
                                                                            m("td", { class: "px-4 py-2" }, 
                                                                                m("span", { class: `px-2 py-1 rounded-full text-xs font-medium ${statusClass}` }, statusText)
                                                                            ),
                                                                            m("td", { class: "px-4 py-2 text-right font-mono text-gray-700 dark:text-gray-300" }, 
                                                                                historyItem.status === 'Paid' ? this.formatCurrency(historyItem.amount) : this.formatCurrency(historyItem.estimatedAmount)
                                                                            ),
                                                                            m("td", { class: "px-4 py-2 text-center" }, 
                                                                                historyItem.status === 'Missing' ?
                                                                                    m("button", {
                                                                                        class: "bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1 rounded-md text-xs transition-colors",
                                                                                        disabled: vnode.state.isProcessing,
                                                                                        onclick: (e) => {
                                                                                            e.stopPropagation();
                                                                                            this.payManual(vnode, account.id, product.id, historyItem.targetDate, historyItem.estimatedAmount);
                                                                                        }
                                                                                    }, [
                                                                                        m("i", { class: "ri-hand-coin-line mr-1" }),
                                                                                        "Bayar Manual"
                                                                                    ])
                                                                                : m("span", { class: "text-gray-400 text-xs italic" }, "-")
                                                                            )
                                                                        ]);
                                                                    })
                                                                ])
                                                            ])
                                                        ])
                                                ]) : null
                                            ]);
                                        })
                                    )
                            ]) : null
                        ]);
                    })
                )
        ]);
    }
};

export default SavingsInterestPayment;
